export interface SeoMetaOptions {
  title: string;
  description?: string;
  keywords?: string;
  image?: string;
  canonicalUrl?: string;
  type?: string;
  structuredData?: JsonLdObject | JsonLdObject[];
}

export interface JsonLdObject {
  [key: string]: any;
}

export interface SeoBreadcrumbItem {
  name: string;
  url: string;
}

export interface SeoTitleOptions {
  primary: unknown;
  siteName?: unknown;
  descriptor?: unknown;
  city?: unknown;
  fallbackPrimary?: string;
}

const SEO_TITLE_MIN_LENGTH = 30;
const SEO_TITLE_MAX_LENGTH = 60;
const SEO_DESCRIPTION_MAX_LENGTH = 155;

class SeoMetaValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SeoMetaValidationError";
  }
}

export function getSeoOrigin(context?: any): string {
  if (typeof window !== "undefined" && window.location && window.location.origin) {
    return window.location.origin;
  }

  if (context && context.$ssrContext && context.$ssrContext.origin) {
    return context.$ssrContext.origin;
  }

  return "";
}

export function getRuntimeSeoConfig(): any {
  if (typeof window !== "undefined" && (window as any).vueAppConfig) {
    return (window as any).vueAppConfig;
  }

  if (typeof globalThis !== "undefined" && (globalThis as any).config) {
    return (globalThis as any).config;
  }

  return {};
}

export function buildCanonicalUrl(context: any): string {
  return `${getSeoOrigin(context)}${context && context.$route ? context.$route.path : ""}`;
}

export function buildSeoMeta(options: SeoMetaOptions) {
  const safeOptions = options || ({ title: "Online Store" } as SeoMetaOptions);

  try {
    const title = normalizeSeoTitle(safeOptions.title);
    const description = normalizeSeoDescription(safeOptions.description, title);

    assertSeoMetaLimits(title, description);

    return createSeoMetaResult(safeOptions, title, description);
  } catch (error) {
    reportSeoMetaValidationError(error);

    const fallbackTitle = normalizeSeoTitle(safeOptions.title || "Online Store");
    const fallbackDescription = normalizeSeoDescription(safeOptions.description, fallbackTitle);

    return createSeoMetaResult(
      {
        ...safeOptions,
        title: fallbackTitle,
        description: fallbackDescription,
        structuredData: []
      },
      fallbackTitle,
      fallbackDescription
    );
  }
}

function createSeoMetaResult(options: SeoMetaOptions, title: string, description: string) {
  const type = options.type || "website";
  const structuredDataList = Array.isArray(options.structuredData)
    ? options.structuredData
    : options.structuredData
      ? [options.structuredData]
      : [];

  return {
    title,
    meta: [
      { vmid: "description", name: "description", content: description },
      { vmid: "keywords", name: "keywords", content: options.keywords || "" },
      { vmid: "og:title", property: "og:title", content: title },
      { vmid: "og:description", property: "og:description", content: description },
      { vmid: "og:url", property: "og:url", content: options.canonicalUrl || "" },
      { vmid: "og:image", property: "og:image", content: options.image || "" },
      { vmid: "og:type", property: "og:type", content: type },
      { vmid: "twitter:title", name: "twitter:title", content: title },
      { vmid: "twitter:description", name: "twitter:description", content: description },
      { vmid: "twitter:image", name: "twitter:image", content: options.image || "" }
    ],
    link: [
      { vmid: "canonical", rel: "canonical", href: options.canonicalUrl || "" }
    ],
    script: structuredDataList.map((structuredData, index) => ({
      vmid: `jsonld-${index}`,
      type: "application/ld+json",
      json: removeEmptyJsonLdValues(structuredData)
    }))
  };
}

function assertSeoMetaLimits(title: string, description: string): void {
  if (title.length < SEO_TITLE_MIN_LENGTH) {
    throw new SeoMetaValidationError(
      `SEO title is shorter than ${SEO_TITLE_MIN_LENGTH} characters after normalization.`
    );
  }

  if (title.length > SEO_TITLE_MAX_LENGTH) {
    throw new SeoMetaValidationError(
      `SEO title is longer than ${SEO_TITLE_MAX_LENGTH} characters after normalization.`
    );
  }

  if (description.length > SEO_DESCRIPTION_MAX_LENGTH) {
    throw new SeoMetaValidationError(
      `SEO description is longer than ${SEO_DESCRIPTION_MAX_LENGTH} characters after normalization.`
    );
  }
}

function reportSeoMetaValidationError(error: any): void {
  if (isProductionRuntime()) return;
  if (typeof console === "undefined" || typeof console.warn !== "function") return;

  const message = error && error.message ? error.message : error;
  console.warn("[SEO] Metadata validation recovered with a safe fallback.", message);
}

function isProductionRuntime(): boolean {
  return typeof process !== "undefined" &&
    !!process.env &&
    process.env.NODE_ENV === "production";
}

export function normalizeSeoDescription(description: unknown, fallbackTitle: string = "Online Store"): string {
  const normalizedDescription = normalizeSeoText(description) || `Shop online with ${fallbackTitle}.`;

  if (normalizedDescription.length <= SEO_DESCRIPTION_MAX_LENGTH) {
    return normalizedDescription;
  }

  return truncateSeoText(normalizedDescription, SEO_DESCRIPTION_MAX_LENGTH);
}

export function buildSeoTitle(options: SeoTitleOptions): string {
  const primary = normalizeSeoTitlePart(options.primary) || options.fallbackPrimary || "Online Store";
  const siteName = normalizeSeoTitlePart(options.siteName);
  const descriptor = normalizeSeoTitlePart(options.descriptor);
  const city = normalizeSeoTitlePart(options.city);
  const parts = [primary, descriptor, siteName, city].reduce((titleParts: string[], part) => {
    if (!part) return titleParts;

    const normalizedPart = part.toLowerCase();
    const alreadyCovered = titleParts.some((existingPart) => {
      const normalizedExistingPart = existingPart.toLowerCase();
      return normalizedExistingPart === normalizedPart || normalizedExistingPart.includes(normalizedPart);
    });

    if (!alreadyCovered) {
      titleParts.push(part);
    }

    return titleParts;
  }, []);

  return normalizeSeoTitle(parts.join(" | "));
}

export function normalizeSeoTitle(title: string): string {
  const normalizedTitle = normalizeSeoTitlePart(title) || "Online Store";
  const expansionParts = ["Online Shopping", "Shop Online"];

  if (normalizedTitle.length > SEO_TITLE_MAX_LENGTH) {
    return truncateSeoTitle(normalizedTitle, SEO_TITLE_MAX_LENGTH);
  }

  if (normalizedTitle.length >= SEO_TITLE_MIN_LENGTH) {
    return normalizedTitle;
  }

  const expandedTitle = [normalizedTitle, ...expansionParts].reduce((titleParts: string[], part) => {
    if (!part) return titleParts;

    const normalizedPart = part.toLowerCase();
    const alreadyCovered = titleParts.some((existingPart) => {
      const normalizedExistingPart = existingPart.toLowerCase();
      return normalizedExistingPart === normalizedPart || normalizedExistingPart.includes(normalizedPart);
    });

    if (!alreadyCovered) {
      titleParts.push(part);
    }

    return titleParts;
  }, []).join(" | ");

  if (expandedTitle.length <= SEO_TITLE_MAX_LENGTH) {
    return expandedTitle;
  }

  return truncateSeoTitle(expandedTitle, SEO_TITLE_MAX_LENGTH);
}

function normalizeSeoTitlePart(value: unknown): string {
  if (typeof value !== "string" && typeof value !== "number") return "";

  return normalizeSeoText(value)
    .replace(/\s+\|\s+/g, " | ");
}

function normalizeSeoText(value: unknown): string {
  if (typeof value !== "string" && typeof value !== "number") return "";

  return String(value)
    .replace(/\s+/g, " ")
    .trim();
}

function truncateSeoTitle(title: string, maxLength: number): string {
  if (title.length <= maxLength) return title;

  const shortened = title.substring(0, maxLength - 3).trim();
  const lastSpaceIndex = shortened.lastIndexOf(" ");
  const safeShortened = lastSpaceIndex > 35 ? shortened.substring(0, lastSpaceIndex).trim() : shortened;

  return `${safeShortened}...`;
}

function truncateSeoText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;

  const shortened = text.substring(0, maxLength - 3).trim();
  const lastSpaceIndex = shortened.lastIndexOf(" ");
  const safeShortened = lastSpaceIndex > 80 ? shortened.substring(0, lastSpaceIndex).trim() : shortened;

  return `${safeShortened}...`;
}

export function formatSeoText(value: unknown): string {
  if (typeof value !== "string") return "";

  return value
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function buildOrganizationJsonLd(options: {
  name: string;
  url: string;
  logo?: string;
  sameAs?: string[];
}): JsonLdObject {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: options.name,
    url: options.url,
    logo: options.logo,
    sameAs: options.sameAs && options.sameAs.length > 0 ? options.sameAs : undefined
  };
}

export function buildWebSiteJsonLd(options: {
  name: string;
  url: string;
}): JsonLdObject {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: options.name,
    url: options.url
  };
}

export function buildStoreJsonLd(options: {
  name: string;
  url: string;
  image?: string;
  telephone?: string;
  address?: {
    streetAddress?: string;
    addressLocality?: string;
    addressRegion?: string;
    addressCountry?: string;
  };
}): JsonLdObject {
  return {
    "@context": "https://schema.org",
    "@type": "Store",
    name: options.name,
    url: options.url,
    image: options.image,
    telephone: options.telephone,
    address: options.address
      ? {
          "@type": "PostalAddress",
          streetAddress: options.address.streetAddress,
          addressLocality: options.address.addressLocality,
          addressRegion: options.address.addressRegion,
          addressCountry: options.address.addressCountry
        }
      : undefined
  };
}

export function buildCollectionPageJsonLd(options: {
  name: string;
  description: string;
  url: string;
  image?: string;
  siteName?: string;
}): JsonLdObject {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: options.name,
    description: options.description,
    url: options.url,
    image: options.image,
    isPartOf: options.siteName
      ? {
          "@type": "WebSite",
          name: options.siteName
        }
      : undefined
  };
}

export function buildProductJsonLd(options: {
  name: string;
  url: string;
  description?: string;
  image?: string;
  brand?: string;
  price?: number;
  currency?: string;
  availability?: string;
}): JsonLdObject | undefined {
  if (!options.name || !options.url) return undefined;

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: options.name,
    description: options.description,
    image: options.image,
    brand: options.brand
      ? {
          "@type": "Brand",
          name: options.brand
        }
      : undefined,
    offers: options.price && options.currency
      ? {
          "@type": "Offer",
          url: options.url,
          priceCurrency: options.currency,
          price: options.price,
          availability: options.availability || "https://schema.org/InStock"
        }
      : undefined
  };
}

export function buildBreadcrumbListJsonLd(items: SeoBreadcrumbItem[]): JsonLdObject | undefined {
  const validItems = items.filter((item) => item.name && item.url);

  if (validItems.length < 2) return undefined;

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: validItems.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  };
}

export function buildBreadcrumbItemsFromRoute(
  route: any,
  router: any,
  origin: string,
  context?: any
): SeoBreadcrumbItem[] {
  const breadcrumbConfig = route && route.meta ? route.meta.breadCrumb : undefined;
  const breadcrumbItems = typeof breadcrumbConfig === "function"
    ? breadcrumbConfig.call(context, route)
    : breadcrumbConfig;

  if (!Array.isArray(breadcrumbItems)) return [];

  return breadcrumbItems
    .map((item: any, index: number) => ({
      name: formatSeoText(item.text) || item.text || "",
      url: resolveBreadcrumbUrl(item, index, breadcrumbItems.length, route, router, origin)
    }))
    .filter((item: SeoBreadcrumbItem) => item.name && item.url);
}

export function stripHtmlForSeo(value: string, maxLength: number = 160): string {
  if (!value) return "";

  const text = value
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (text.length <= maxLength) return text;

  return `${text.substring(0, maxLength - 1).trim()}…`;
}

function removeEmptyJsonLdValues(value: any): any {
  if (Array.isArray(value)) {
    return value
      .map(removeEmptyJsonLdValues)
      .filter((item) => item !== undefined && item !== null && item !== "");
  }

  if (value && typeof value === "object") {
    return Object.keys(value).reduce((cleaned: JsonLdObject, key: string) => {
      const cleanedValue = removeEmptyJsonLdValues(value[key]);

      if (
        cleanedValue !== undefined &&
        cleanedValue !== null &&
        cleanedValue !== "" &&
        (!Array.isArray(cleanedValue) || cleanedValue.length > 0)
      ) {
        cleaned[key] = cleanedValue;
      }

      return cleaned;
    }, {});
  }

  return value;
}

function resolveBreadcrumbUrl(
  item: any,
  index: number,
  totalItems: number,
  route: any,
  router: any,
  origin: string
): string {
  if (!item.to || index === totalItems - 1) {
    return `${origin}${route.path}`;
  }

  try {
    const target = typeof item.to === "object"
      ? {
          ...item.to,
          params: {
            ...(route.params || {}),
            ...(item.to.params || {})
          }
        }
      : item.to;
    const resolvedRoute = router.resolve(target);
    return buildAbsoluteUrl(origin, resolvedRoute.href);
  } catch (error) {
    return `${origin}${route.path}`;
  }
}

function buildAbsoluteUrl(origin: string, path: string): string {
  if (!path) return origin;
  if (/^https?:\/\//.test(path)) return path;

  return `${origin}${path.startsWith("/") ? path : `/${path}`}`;
}
